import { PageId } from 'constants/pages'
import i18n from 'i18next'
import { isEmpty } from 'lodash'
import { OnboardingType } from 'models/lead.model'
import {
  NOT_UPSELL_LOCALIZATIONS,
  PaymentSystem,
  PIT_PAT_UPSELL_FLOW_KEYS,
} from 'nove8/constants/payment'
import { TStep } from 'nove8/types'
import { Countries, LOCALES } from 'root-constants'
import { StatesConfig } from 'xstate'
import { useZustandGlobalLocalStore } from 'zustandGlobalLocalStore'
import { useZustandSessionStore } from 'zustandGlobalSessionStore'
import { useZustandGlobalStore } from 'zustandGlobalStore'
import { getHasBasicSubscription } from '../../helpers/getHasBasicSubscription'

const getHasEmail = () => !!useZustandGlobalStore.getState().lead?.email
const getHasSubscription = () =>
  getHasBasicSubscription(
    useZustandGlobalStore.getState().lead?.subscriptions || null,
  )
const getHasAccount = () =>
  !!useZustandGlobalStore.getState().lead?.firebaseToken
const getHasAnswers = () =>
  !isEmpty(
    useZustandSessionStore.getState().userAnswers ||
      useZustandGlobalStore.getState().lead?.quizzes ||
      null,
  )
const isPitPatUpsellAvailable = () =>
  i18n.language === LOCALES.EN &&
  useZustandGlobalStore.getState().lead?.country === Countries.GB &&
  PIT_PAT_UPSELL_FLOW_KEYS.includes(
    useZustandGlobalStore
      .getState()
      .firebaseRemoteConfig?.getValue('upsell_flow_type')
      ?.asString() as string,
  )

const isPaypalPayment = () =>
  ((useZustandGlobalStore.getState().lead?.subscriptions?.[0]
    ?.paymentSystemType as unknown) as PaymentSystem) === PaymentSystem.PAYPAL

const isCancelOfferOnboarding = () =>
  useZustandGlobalLocalStore.getState().selectedLeadPlan?.onboardingType ===
  OnboardingType.CANCEL_OFFER

const isEnLocale = () => {
  const langFromUrl = new URL(window.location.href).searchParams.get('lang')

  return (
    (langFromUrl || useZustandSessionStore.getState().language) === LOCALES.EN
  )
}

export const getXStateConfigWoofz22V3 = (
  pages: TStep[],
): StatesConfig<any, any, any> => ({
  initial: {
    // Pay attention to the order of the conditions!
    always: [
      // Cover case when page was reloaded or connection was lost
      // during the purchase polling
      {
        cond: (): boolean => !!useZustandGlobalLocalStore.getState().purchaseId,
        target:
          useZustandGlobalLocalStore.getState().purchasePageId ||
          pages[0].pageId,
      },
      {
        cond: (): boolean => {
          const pageIdFromUrl = window.location.pathname.split('/').pop() || ''
          return (
            !getHasEmail() &&
            !getHasSubscription() &&
            !getHasAccount() &&
            getHasAnswers() &&
            pageIdFromUrl === PageId.NEWSLETTER_URGE_22V3
          )
        },
        target: PageId.EMAIL_22V3,
      },
      // Do not redirect users to the next breakpoint before email page
      {
        cond: (): boolean => {
          const hasEmail = getHasEmail()
          const hasSubscription = getHasSubscription()
          const hasAccount = getHasAccount()
          const hasAnswers = getHasAnswers()
          return !hasEmail && !hasSubscription && !hasAccount && hasAnswers
        },
        target:
          pages.find((dynamicPage) => {
            const pageIdFromUrl =
              window.location.pathname.split('/').pop() || ''
            return pageIdFromUrl === dynamicPage.pageId
          })?.pageId || (isEnLocale() ? PageId.DOG_AGE_22V3 : pages[0].pageId),
      },
      {
        cond: (): boolean => getHasAccount(),
        target: PageId.DOWNLOAD_22V3,
      },
      {
        cond: (): boolean => getHasSubscription(),
        target: PageId.ACCOUNT_NOVE8,
      },
      {
        cond: (): boolean => getHasEmail(),
        target: PageId.SUBSCRIPTIONS_22V3,
      },
      {
        cond: isEnLocale,
        target: PageId.DOG_AGE_22V3,
      },
      {
        target: pages[0].pageId, // Always transition to first page as fallback
      },
    ],
  },
  ...pages.reduce(
    (acc, page, index) => ({
      ...acc,
      [page.pageId]: {
        on: {
          NEXT_FLOW_STEP: {
            target: pages[index + 1]?.pageId,
          },
          SKIP_ONE_STEP: {
            target: pages[index + 2]?.pageId,
          },
          PREVIOUS_FLOW_STEP: {
            target:
              index - 1 >= 0 ? pages[index - 1].pageId : pages[index].pageId,
          },
          SKIP_ONE_STEP_BACK: {
            target:
              index - 2 >= 0 ? pages[index - 2].pageId : pages[index].pageId,
          },
          ...(page.pageId === PageId.SEX_22V3 && {
            NEXT_FLOW_STEP: [
              { cond: isEnLocale, target: PageId.DOG_BREED_22V3 },
              { target: pages[index + 1]?.pageId },
            ],
            PREVIOUS_FLOW_STEP: [
              { cond: isEnLocale, target: PageId.DOG_AGE_22V3 },
              {
                target: pages[index].pageId,
              },
            ],
          }),
          ...(page.pageId === PageId.DOG_AGE_22V3 && {
            NEXT_FLOW_STEP: [
              { cond: isEnLocale, target: PageId.SEX_22V3 },
              { target: pages[index + 1]?.pageId },
            ],
            PREVIOUS_FLOW_STEP: [
              { cond: isEnLocale, target: PageId.DOG_AGE_22V3 },
              {
                target:
                  index - 1 >= 0
                    ? pages[index - 1].pageId
                    : pages[index].pageId,
              },
            ],
          }),
          ...(page.pageId === PageId.DOG_BREED_22V3 && {
            PREVIOUS_FLOW_STEP: [
              { cond: isEnLocale, target: PageId.SEX_22V3 },
              {
                target:
                  index - 1 >= 0
                    ? pages[index - 1].pageId
                    : pages[index].pageId,
              },
            ],
          }),
          ...(page.pageId === PageId.URGE_22V3 && {
            PREVIOUS_FLOW_STEP: {
              target: PageId.DOG_BREED_22V3,
            },
          }),
          ...(page.pageId === PageId.PAYMENT_22V3 && {
            NEXT_FLOW_STEP: [
              {
                cond: isPaypalPayment,
                target: PageId.ACCOUNT_NOVE8,
              },
              {
                target: PageId.CHAT_UPSELL_22V3,
              },
            ],
            PREVIOUS_FLOW_STEP: [
              {
                cond: isCancelOfferOnboarding,
                target: PageId.CANCEL_OFFER_22V3,
              },
              {
                target: PageId.LAST_CHANCE_OFFER_2_22V3,
              },
            ],
          }),
          ...(page.pageId === PageId.LAST_CHANCE_OFFER_2_22V3 && {
            NEXT_FLOW_STEP: {
              target: PageId.CANCEL_OFFER_22V3,
            },
          }),
          ...(page.pageId === PageId.CANCEL_OFFER_22V3 && {
            NEXT_FLOW_STEP: {
              target: PageId.PAYMENT_22V3,
            },
            PREVIOUS_FLOW_STEP: {
              target: PageId.PAYMENT_22V3,
            },
          }),
          ...(page.pageId === PageId.CHAT_UPSELL_22V3 && {
            NEXT_FLOW_STEP: [
              {
                cond: () => {
                  const url = new URL(window.location.href)
                  const languageFromUrl = url.searchParams.get('lang')
                  return NOT_UPSELL_LOCALIZATIONS.includes(
                    languageFromUrl as string,
                  )
                },
                target: PageId.ACCOUNT_NOVE8,
              },
              {
                target: PageId.UPSELL_22V3,
              },
            ],
          }),
          ...(page.pageId === PageId.UPSELL_22V3 && {
            NEXT_FLOW_STEP: [
              {
                cond: isEnLocale,
                target: PageId.UPSELL_FEEDING_GUIDE_22V3,
              },
              {
                cond: isPitPatUpsellAvailable,
                target: PageId.UPSELL_PITPAT_22V3,
              },
              {
                target: PageId.ACCOUNT_NOVE8,
              },
            ],
          }),
          ...(page.pageId === PageId.UPSELL_FEEDING_GUIDE_22V3 && {
            NEXT_FLOW_STEP: PageId.UPSELL_EMOTIONAL_SUPPORT_22V3,
          }),
          ...(page.pageId === PageId.UPSELL_EMOTIONAL_SUPPORT_22V3 && {
            NEXT_FLOW_STEP: [
              {
                cond: isPitPatUpsellAvailable,
                target: PageId.UPSELL_PITPAT_22V3,
              },
              {
                target: PageId.ACCOUNT_NOVE8,
              },
            ],
          }),
        },
      },
    }),
    {},
  ),
  [PageId.ACCOUNT_NOVE8]: {
    on: {
      NEXT_FLOW_STEP: PageId.DOWNLOAD_22V3,
    },
  },
  [PageId.DOWNLOAD_22V3]: {
    type: 'final',
  },
})
